import { ChangeDetectorRef, Component, Input, OnInit } from "@angular/core";
import { Branch } from "../../../model/branches";
import { BranchDetailsService } from "../../../services/branch-details.service";
import { BehaviorSubject } from "rxjs";
import { Messages } from "../../../model/messages";
import { AuthService } from "../../../auth.service";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActionButtonSubmit } from "../../../model/action-button-submit";
import { BranchService } from "../../../services/branch.service";
import { Router } from "@angular/router";
import { NavigationHelper } from "../../../services/navigation.helper";
import { Navigation } from "../../../model/navigation";
import { LoggedIn } from "../../../model/loggedIn";

@Component({
    selector: "branch-details",
    templateUrl: "./branch-details.component.html",
    styleUrls: ["./branch-details.component.css"],
})
export class BranchDetailsComponent implements OnInit {
    messages: BehaviorSubject<Messages>;
    branches: BehaviorSubject<Branch>;
    editMode: boolean = false;
    branchForm: FormGroup;
    branchTitle: string = "Branch Details";
    @Input() branch: any;
    isLoading: boolean = false;
    supervisor: boolean = false;
    constructor(
        private branchService: BranchService,
        private branchDetailsService: BranchDetailsService,
        private auth: AuthService,
        private fb: FormBuilder,
        private router: Router,
        private nav: NavigationHelper,
        private cd: ChangeDetectorRef
    ) {
        this.branch = new BehaviorSubject<Branch>(Branch.EMPTY);
        this.messages = new BehaviorSubject<Messages>(Messages.EMPTY);
    }

    ngOnInit(): void {
        this.initializeForm();

        this.supervisor = this.auth.isSupervisor();

        if (Navigation.NEW.toString() === this.nav.currentId) {
            this.editMode = true;
            this.branchForm.reset();
            this.branchTitle = "New Branch";
        }

        this.branchDetailsService.branchObservable.subscribe((data) => {
            if (data) {
                this.branch.next(data);
                this.messages.next(Messages.EMPTY);
                this.editMode = data.id === Navigation.NEW;
                this.branchTitle = this.editMode
                    ? "branchDetails.title.new.branch"
                    : "branchDetails.title.exist.branch";
                this.branchForm.patchValue({
                    id: data.id || "",
                    name: data.name || "",
                    abbreviation: data.abbreviation || "",
                });
            } else {
            }
        });
        const selected = this.branchDetailsService.selected;
        if (selected && selected.id) {
            this.branch.next(selected);
            this.branchForm.patchValue({
                id: selected.id,
                name: selected.name,
                abbreviation: selected.abbreviation
            });
        }

        if (
            !(
                this.branchDetailsService.selected &&
                this.branchDetailsService.selected.id
            )
        ) {
            this.branchDetailsService.selectFromRouter();
        }
        this.nav.id.subscribe((id) => {
            this.branchDetailsService.selectFromRouter();
        });
    }
    private initializeForm(): void {
        this.branchForm = this.fb.group({
            id: [{ value: 0, disabled: true }],
            name: "",
            abbreviation: "",
            messages: this.messages,
        });
    }

    loadBranchDetails(branchId: string) {
        const token = this.auth.token;
        this.branchDetailsService
            .getBranchById(token, +branchId)
            .subscribe((messages) => {
                if (messages) {
                    const branchData = this.branchDetailsService.selected;
                    this.branchDetailsService.select(branchData);
                }
            });
    }

    BranchEmpty(): boolean {
        const branchValue = this.branch.value;
        return (
            (!branchValue || branchValue.id === null || branchValue.id === 0) &&
            !this.editMode
        );
    }

    toggleEditMode() {
        if (this.editMode) {
            this.editMode = false;
            this.messages.next(Messages.EMPTY);
            const selected = this.branchDetailsService.selected;
            if (selected && selected.id) {
                this.branch.next(selected);
                this.branchForm.patchValue({
                    name: selected.name || '',
                    abbreviation: selected.abbreviation || ''
                });
                this.branchTitle = "branchDetails.title.exist.branch";
            } else {
                this.branch.next(Branch.EMPTY);
                this.branchForm.reset();
            }
        } else {
            this.editMode = true;
        }
    }

    saveBranch(action: ActionButtonSubmit): void {
        this.isLoading = true;

        this.messages.next(Messages.EMPTY);

        const name = this.branchForm.value.name?.trim();
        const abbreviation = this.branchForm.value.abbreviation?.trim();

        const newMessages = Messages.from({
            name: !name ? ["branchDetails.nameRequired"] : [],
            abbreviation: !abbreviation ? ["branchDetails.abbreviationRequired"] : []
        });

        if (newMessages.hasAnyMessages) {
            this.isLoading = false;
            this.messages.next(newMessages);
            action.result(false);
            return;
        }

        const token = this.auth.token;
        const id = this.branch.value.id;

        const updatedBranch = new Branch(
            id == null || id < 1 ? null : id,
            name!,
            abbreviation!
        );


        const onSuccess = (messages: Messages) => {
            this.isLoading = false;
            this.messages.next(messages);

            if (messages.hasAnyMessages) {
                action.result(false);
                return;
            }

            if (updatedBranch.id == null) {
                const createdBranch = this.branchDetailsService.selected;
                if (createdBranch.id) {
                    const currentBranches = this.branchService.branchesData;
                    currentBranches.push(createdBranch);
                    this.branchService._currentBranches.next(currentBranches);
                }
                this.branchDetailsService.select(createdBranch);
            } else {
                const currentBranches = this.branchService.branchesData;
                const index = currentBranches.findIndex(
                    (branch) => branch.id === updatedBranch.id
                );
                if (index !== -1) {
                    currentBranches[index] = updatedBranch;
                    this.branchService._currentBranches.next(currentBranches);
                }
                this.branchDetailsService.select(updatedBranch);
            }

            action.result(true);
        };

        const onError = (error: any) => {
            this.isLoading = false;
            action.result(false);
            this.messages.next(
                Messages.from({ branch: ["branch.unknown.error", error] })
            );
        };

        if (updatedBranch.id == null) {
            this.branchDetailsService
                .createBranch(token, updatedBranch)
                .subscribe(onSuccess, onError);
        } else {
            this.branchDetailsService
                .updateBranch(token, updatedBranch)
                .subscribe(onSuccess, onError);
        }
    }

    deleteBranch() {
        const token = this.auth.token;
        const branchId = this.branch.value.id;

        if (branchId != null) {
            this.branchDetailsService.deleteBranch(token, branchId).subscribe(
                (messages) => {
                    this.messages.next(messages);

                    if (!messages.hasAnyMessages) {
                        const currentBranches = this.branchService.branchesData;
                        const updatedBranches = currentBranches.filter(
                            (branch) => branch.id !== branchId
                        );
                        this.branchService._currentBranches.next(updatedBranches);

                        this.branch.next(Branch.EMPTY);
                        this.branchForm.reset();
                        this.editMode = false;

                        this.router.navigate(["/branches"]);
                    }
                },
                (messages) => {
                    this.messages.next(messages);
                }
            );
        }
    }

    get TRUE(): boolean {
        return true;
    }
    get FALSE(): boolean {
        return false;
    }
}
